/**
 * SpeakerMappingPanel (DC-1603)
 *
 * Lists every speaker parsed from a `category='group_transcript'` governance
 * document and lets the consultant map each speaker to a persona via the
 * PATCH /api/group-transcripts/:documentId/speakers/:speakerId/persona route.
 *
 * Rendered inside `DocumentDetailPanel` when (and only when) the parent
 * document's category is `group_transcript`. The panel is read-only for any
 * other category — the parser hasn't been run, no speakers exist.
 *
 * Visual intent: utility-grade for v1.2.0. The polished Larissa-designed
 * surface lands in v1.3 (see compass-ux open architectural questions).
 * Until then, this matches the existing organizational-interviews styling
 * vocabulary so it slots in without theme drift.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';

interface Persona {
  id: string;
  name: string;
  archetype: string | null;
}

interface GroupSessionSpeaker {
  id: string;
  org_id: string;
  document_id: string;
  persona_id: string | null;
  speaker_name: string;
  statement_count: number;
  fair_heatmap: Record<string, unknown> | null;
  analyzed_at: string | null;
  created_at: string;
  updated_at: string;
}

interface SpeakerMappingPanelProps {
  documentId: string;
  /** Visible only when the parent document has category 'group_transcript'. */
  category: string;
}

const UNMAPPED_VALUE = '';

export const SpeakerMappingPanel: React.FC<SpeakerMappingPanelProps> = ({
  documentId,
  category,
}) => {
  const { get, patch, post } = useApiClient();

  const [speakers, setSpeakers] = useState<GroupSessionSpeaker[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingSpeakerId, setSavingSpeakerId] = useState<string | null>(null);
  const [analyzingSpeakerId, setAnalyzingSpeakerId] = useState<string | null>(null);
  const [analyzeAllRunning, setAnalyzeAllRunning] = useState(false);
  // Per-speaker analyze errors, keyed by speaker id. The analyze route passes
  // the real upstream message through (e.g. "Azure OpenAI not configured…"),
  // so we surface it inline rather than masking it.
  const [analyzeErrors, setAnalyzeErrors] = useState<Record<string, string>>({});

  const isGroupTranscript = category === 'group_transcript';

  const load = useCallback(async () => {
    if (!isGroupTranscript) return;
    setLoading(true);
    setError(null);
    try {
      const [speakersRes, personasRes] = await Promise.all([
        get<GroupSessionSpeaker[]>(`/api/group-transcripts/${documentId}/speakers`),
        get<Persona[]>('/api/personas'),
      ]);
      setSpeakers(Array.isArray(speakersRes) ? speakersRes : []);
      setPersonas(Array.isArray(personasRes) ? personasRes : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSpeakers([]);
      setPersonas([]);
    } finally {
      setLoading(false);
    }
  }, [documentId, get, isGroupTranscript]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleMappingChange = useCallback(
    async (speakerId: string, selected: string): Promise<void> => {
      const personaId = selected === UNMAPPED_VALUE ? null : selected;
      setSavingSpeakerId(speakerId);
      setError(null);
      try {
        const updated = await patch<GroupSessionSpeaker>(
          `/api/group-transcripts/${documentId}/speakers/${speakerId}/persona`,
          { persona_id: personaId },
        );
        setSpeakers((prev) =>
          prev.map((s) => (s.id === speakerId ? { ...s, persona_id: updated.persona_id } : s)),
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setSavingSpeakerId(null);
      }
    },
    [documentId, patch],
  );

  /**
   * Analyze one speaker. Returns true on success. On failure, stores the real
   * server message in `analyzeErrors[speakerId]` (no throw) so a batch run can
   * continue past a single failure. Does NOT touch persona_id.
   */
  const analyzeOne = useCallback(
    async (speakerId: string): Promise<boolean> => {
      setAnalyzeErrors((prev) => {
        if (!(speakerId in prev)) return prev;
        const next = { ...prev };
        delete next[speakerId];
        return next;
      });
      try {
        const updated = await post<GroupSessionSpeaker>(
          `/api/group-transcripts/${documentId}/speakers/${speakerId}/analyze`,
        );
        setSpeakers((prev) =>
          prev.map((s) =>
            s.id === speakerId
              ? { ...s, analyzed_at: updated.analyzed_at, fair_heatmap: updated.fair_heatmap }
              : s,
          ),
        );
        return true;
      } catch (err) {
        setAnalyzeErrors((prev) => ({
          ...prev,
          [speakerId]: err instanceof Error ? err.message : String(err),
        }));
        return false;
      }
    },
    [documentId, post],
  );

  const handleAnalyze = useCallback(
    async (speakerId: string): Promise<void> => {
      setAnalyzingSpeakerId(speakerId);
      try {
        await analyzeOne(speakerId);
      } finally {
        setAnalyzingSpeakerId(null);
      }
    },
    [analyzeOne],
  );

  const handleAnalyzeAllPending = useCallback(async (): Promise<void> => {
    // Sequential by design: each call is an Azure OpenAI round-trip. Serial
    // keeps us under the per-deployment TPM ceiling and gives a predictable
    // one-at-a-time progression. The panel is capped at 50 speakers upstream.
    const pending = speakers.filter((s) => !s.analyzed_at);
    if (pending.length === 0) return;
    setAnalyzeAllRunning(true);
    try {
      for (const speaker of pending) {
        setAnalyzingSpeakerId(speaker.id);
        await analyzeOne(speaker.id);
      }
    } finally {
      setAnalyzingSpeakerId(null);
      setAnalyzeAllRunning(false);
    }
  }, [speakers, analyzeOne]);

  const pendingCount = speakers.filter((s) => !s.analyzed_at).length;

  if (!isGroupTranscript) return null;

  return (
    <section className="doc-panel__section" data-testid="speaker-mapping-panel">
      <div className="doc-panel__section-header">
        <h3>Speaker → Persona Mapping</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span
            style={{
              fontSize: '0.75rem',
              color: 'var(--muted)',
            }}
          >
            {speakers.length === 0
              ? 'No speakers'
              : `${speakers.filter((s) => s.persona_id).length} of ${speakers.length} mapped`}
          </span>
          {!loading && pendingCount > 0 && (
            <button
              type="button"
              data-testid="speaker-analyze-all"
              onClick={() => {
                void handleAnalyzeAllPending();
              }}
              disabled={analyzeAllRunning || analyzingSpeakerId !== null}
              style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                padding: '0.3125rem 0.625rem',
                borderRadius: '0.375rem',
                border: '1px solid var(--primary)',
                background: 'var(--primary)',
                color: 'var(--text)',
                cursor: analyzeAllRunning || analyzingSpeakerId !== null ? 'wait' : 'pointer',
                opacity: analyzeAllRunning || analyzingSpeakerId !== null ? 0.7 : 1,
                whiteSpace: 'nowrap',
              }}
            >
              {analyzeAllRunning ? 'Analyzing…' : `Analyze all pending (${pendingCount})`}
            </button>
          )}
        </div>
      </div>

      <p
        style={{
          fontSize: '0.8125rem',
          color: 'var(--muted)',
          marginBottom: '0.75rem',
          lineHeight: 1.5,
        }}
      >
        Map each parsed speaker to a persona to route their statements into the
        Perceived leg of the Triangulated FAIR Assessment. Unmapped speakers
        contribute only to the Governed leg.
      </p>

      {loading && (
        <div className="doc-panel__loading">Loading speakers...</div>
      )}

      {error && !loading && (
        <div className="doc-panel__error" style={{ marginBottom: '0.5rem' }}>
          {error}
        </div>
      )}

      {!loading && !error && speakers.length === 0 && (
        <div className="doc-panel__empty">
          <p>No speakers were extracted from this transcript.</p>
          <p className="doc-panel__empty-hint">
            Check the upload-response warnings for parser feedback. The
            transcript must be in Microsoft Teams plain-text format (a line
            like <code>Speaker Name   0:00</code> above each statement) or
            WebVTT.
          </p>
        </div>
      )}

      {!loading && !error && speakers.length > 0 && (
        <div
          role="table"
          aria-label="Speaker to persona mapping"
          style={{
            display: 'grid',
            gridTemplateColumns: '1.4fr 0.6fr 1fr 1fr',
            gap: '0.5rem 0.75rem',
            fontSize: '0.875rem',
          }}
        >
          <div
            role="row"
            style={{
              display: 'contents',
              fontWeight: 600,
              color: 'var(--muted)',
            }}
          >
            <div role="columnheader">Speaker</div>
            <div role="columnheader" style={{ textAlign: 'right' }}>
              Statements
            </div>
            <div role="columnheader">Persona</div>
            <div role="columnheader">Analyzed</div>
          </div>

          {speakers.map((speaker) => (
            <div key={speaker.id} role="row" style={{ display: 'contents' }}>
              <div
                role="cell"
                style={{
                  color: 'var(--text)',
                  alignSelf: 'center',
                  fontWeight: 500,
                }}
              >
                {speaker.speaker_name}
              </div>
              <div
                role="cell"
                style={{ textAlign: 'right', color: 'var(--text-secondary)', alignSelf: 'center' }}
              >
                {speaker.statement_count}
              </div>
              <div role="cell">
                <select
                  data-testid={`speaker-persona-select-${speaker.id}`}
                  value={speaker.persona_id ?? UNMAPPED_VALUE}
                  disabled={savingSpeakerId === speaker.id || personas.length === 0}
                  onChange={(e) => {
                    void handleMappingChange(speaker.id, e.target.value);
                  }}
                  style={{
                    width: '100%',
                    padding: '0.375rem 0.5rem',
                    fontSize: '0.8125rem',
                    border: '1px solid var(--line)',
                    borderRadius: '0.375rem',
                    // Use --bg (directly overridden light by `.settings-page *`)
                    // NOT --input-bg: the latter is `var(--pane)` computed once
                    // on :root against the dark root --pane and inherits as a
                    // fixed dark value, so the settings light theme never reaches
                    // it — that's what kept this control near-black. colorScheme
                    // 'light' keeps the native popup list readable too.
                    background: 'var(--bg)',
                    color: 'var(--text)',
                    colorScheme: 'light',
                  }}
                >
                  <option value={UNMAPPED_VALUE}>— Unmapped (Governed only) —</option>
                  {personas.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.archetype ? ` (${p.archetype})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div
                role="cell"
                style={{
                  fontSize: '0.75rem',
                  alignSelf: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.25rem',
                }}
              >
                {speaker.analyzed_at ? (
                  <span style={{ color: 'var(--success-text)' }}>
                    {new Date(speaker.analyzed_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                ) : (
                  <button
                    type="button"
                    data-testid={`speaker-analyze-${speaker.id}`}
                    onClick={() => {
                      void handleAnalyze(speaker.id);
                    }}
                    disabled={analyzingSpeakerId === speaker.id || analyzeAllRunning}
                    style={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      padding: '0.25rem 0.5rem',
                      borderRadius: '0.375rem',
                      border: '1px solid var(--primary)',
                      background: 'var(--primary)',
                      color: 'var(--text)',
                      cursor:
                        analyzingSpeakerId === speaker.id || analyzeAllRunning ? 'wait' : 'pointer',
                      opacity:
                        (analyzingSpeakerId === speaker.id || analyzeAllRunning) ? 0.7 : 1,
                      width: 'fit-content',
                    }}
                  >
                    {analyzingSpeakerId === speaker.id ? 'Analyzing…' : 'Analyze'}
                  </button>
                )}
                {analyzeErrors[speaker.id] && (
                  <span
                    data-testid={`speaker-analyze-error-${speaker.id}`}
                    style={{ color: 'var(--error)', fontSize: '0.7rem', lineHeight: 1.3 }}
                  >
                    {analyzeErrors[speaker.id]}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && !error && personas.length === 0 && speakers.length > 0 && (
        <p
          style={{
            marginTop: '0.5rem',
            fontSize: '0.75rem',
            color: 'var(--warning-text)',
          }}
        >
          No personas defined yet. Seed or create personas in Settings →
          Team Management → FAIR Personas before mapping speakers.
        </p>
      )}
    </section>
  );
};

export default SpeakerMappingPanel;
